export const ATTENDANCE_FORMAT_CODES = {
  EDUCATIONAL_INSTITUTION: 'FO-GINF-083',
  COMMUNITY_ORGANIZATION: 'FO-GINF-093',
  SOCIAL_CONTROL: 'FO-GINF-086'
};

const STANDARD_MEDIATOR_ROLE_IDS = new Set([1, 3]);
const SOCIAL_CONTROL_MEDIATOR_ROLE_ID = 6;
const COMMUNITY_SCHOOL_TYPES = new Set(['JAC', 'JAL', 'OS', 'COLECTIVO']);

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
}

function getRoleId(user) {
  const value = Number(user?.roleId ?? user?.role_id ?? user?.role?.id);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function getRoleName(user) {
  return normalizeText(user?.role?.name ?? user?.roleName ?? user?.role_name);
}

function getGroupOwner(meeting) {
  const candidates = [
    meeting?.group?.user,
    meeting?.activityGroup?.user,
    meeting?.activity_group?.user,
    meeting?.school?.user,
    meeting?.group?.school?.user,
    meeting?.user
  ];

  return (
    candidates.find(user => getRoleId(user) || getRoleName(user)) ||
    null
  );
}

function getMediatorKind(meeting) {
  const owner = getGroupOwner(meeting);
  const roleId = getRoleId(owner);
  const roleName = getRoleName(owner);

  if (
    roleId === SOCIAL_CONTROL_MEDIATOR_ROLE_ID ||
    roleName.includes('MEDIADOR CONTROL SOCIAL')
  ) {
    return 'SOCIAL_CONTROL';
  }

  if (
    STANDARD_MEDIATOR_ROLE_IDS.has(roleId) ||
    roleName === 'MEDIADOR' ||
    roleName.includes('MEDIADOR NDN') ||
    roleName.includes('NADA DE NERVIOS')
  ) {
    return 'STANDARD';
  }

  return '';
}

function getSchoolType(meeting) {
  const rawType =
    meeting?.group?.school?.type ??
    meeting?.school?.type ??
    meeting?.group?.schoolType ??
    meeting?.group?.school_type;
  const normalizedType = normalizeText(rawType);

  if (
    normalizedType === 'IE' ||
    normalizedType.includes('INSTITUCION EDUCATIVA')
  ) {
    return 'IE';
  }

  if (
    COMMUNITY_SCHOOL_TYPES.has(normalizedType) ||
    (
      normalizedType.includes('ORGANIZACION') &&
      normalizedType.includes('SOCIAL')
    )
  ) {
    return normalizedType === 'JAC' || normalizedType === 'JAL'
      ? normalizedType
      : 'OS';
  }

  return '';
}

function unwrapParticipant(record) {
  return record?.beneficiary || record || {};
}

function parseCalendarDate(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) return null;

  return date;
}

function getParticipantAge(record, meeting) {
  const participant = unwrapParticipant(record);
  const birthDate = parseCalendarDate(
    participant?.birthDate ?? participant?.birth_date ?? participant?.birthdate
  );

  if (birthDate) {
    const referenceDate = parseCalendarDate(
      meeting?.meetingDate ?? meeting?.meeting_date
    ) || new Date();
    let age = referenceDate.getFullYear() - birthDate.getFullYear();
    if (
      referenceDate.getMonth() < birthDate.getMonth() ||
      (
        referenceDate.getMonth() === birthDate.getMonth() &&
        referenceDate.getDate() < birthDate.getDate()
      )
    ) age -= 1;
    return age >= 0 ? age : null;
  }

  const rawAge = participant?.age;
  if (rawAge === null || rawAge === undefined || String(rawAge).trim() === '') {
    return null;
  }
  const storedAge = Number(rawAge);
  return Number.isFinite(storedAge) && storedAge >= 0 ? storedAge : null;
}

function getCommunityAttendanceFormat(meeting, participants) {
  const ages = (Array.isArray(participants) ? participants : [])
    .map(participant => getParticipantAge(participant, meeting));

  if (ages.some(age => age !== null && age >= 18)) {
    return ATTENDANCE_FORMAT_CODES.COMMUNITY_ORGANIZATION;
  }

  if (ages.length > 0 && ages.every(age => age !== null && age < 18)) {
    return ATTENDANCE_FORMAT_CODES.EDUCATIONAL_INSTITUTION;
  }

  return ATTENDANCE_FORMAT_CODES.COMMUNITY_ORGANIZATION;
}

export function getAllowedAttendanceFormatCodes(meeting, participants = []) {
  if (normalizeText(meeting?.type ?? meeting?.meeting_type).startsWith('COP_')) {
    return [ATTENDANCE_FORMAT_CODES.COMMUNITY_ORGANIZATION];
  }

  const programScope = normalizeText(
    meeting?.school?.programScope ??
    meeting?.school?.program_scope ??
    meeting?.group?.school?.programScope ??
    meeting?.group?.school?.program_scope
  );
  if (programScope === 'DIPLOM') {
    return [ATTENDANCE_FORMAT_CODES.COMMUNITY_ORGANIZATION];
  }

  const mediatorKind = getMediatorKind(meeting);

  if (mediatorKind === 'SOCIAL_CONTROL') {
    return [ATTENDANCE_FORMAT_CODES.SOCIAL_CONTROL];
  }

  if (mediatorKind !== 'STANDARD') {
    return [];
  }

  const schoolType = getSchoolType(meeting);
  if (schoolType === 'IE') {
    return [ATTENDANCE_FORMAT_CODES.EDUCATIONAL_INSTITUTION];
  }

  if (COMMUNITY_SCHOOL_TYPES.has(schoolType)) {
    return [getCommunityAttendanceFormat(meeting, participants)];
  }

  return [];
}
